/**
 * Resolves `PipelineActivityEntry.actorUserId` to a human display name
 * (SE-5504).
 *
 * The activity log carries only a raw `actorUserId`, and the schema exposes no
 * batch user-by-ids query. Rather than adding a per-row `getUser` N+1 (the
 * shape `ReviewQueueRowContainer` had to accept for players), the audit trail
 * reuses the ONE `ListKanbanOrganizationUsers` query the board sharing sheet
 * and note auto-share already run — every actor on a pipeline is by definition
 * a member of the viewer's organization, so a single org roster read resolves
 * every human actor on the page.
 *
 * An id that is not in the roster (a departed member, or a user in another org
 * whose entry is visible) resolves to nothing and the row falls back to the
 * actor-TYPE label — never to a raw UUID.
 * @module features/player-kanban/utils/pipelineActorNames
 */
import type { KanbanOrganizationUserFragment } from "@/generated/graphql";

/** A Cognito attribute pair as the generated fragment shapes it. */
interface CognitoAttribute {
  /** Attribute name, e.g. `given_name`. */
  readonly Name: string;
  /** Attribute value. */
  readonly Value: string;
}

/**
 * Read the attribute list off a Cognito user, preferring `Attributes`.
 * @param orgUser - The organization-user fragment.
 * @returns The attribute list, or an empty list.
 */
const attributesOf = (
  orgUser: KanbanOrganizationUserFragment
): readonly CognitoAttribute[] =>
  orgUser.user?.cognitoUser?.Attributes ??
  orgUser.user?.cognitoUser?.UserAttributes ??
  [];

/**
 * Look one Cognito attribute up by name.
 * @param attributes - The attribute list.
 * @param name - The attribute name.
 * @returns The value, or an empty string.
 */
const attributeValue = (
  attributes: readonly CognitoAttribute[],
  name: string
): string => attributes.find(attr => attr.Name === name)?.Value ?? "";

/**
 * Derive one org user's display name.
 *
 * Same precedence as `BoardShareSheetContainer.getOrgUserDisplayName` — full
 * name, then email — but WITHOUT its `User <uuid-prefix>` fallback: an audit
 * trail that prints a truncated UUID is worse than one that says who the actor
 * was in kind. Callers get `null` and render the actor-type label instead.
 * @param orgUser - The organization-user fragment.
 * @returns The display name, or `null` when nothing human is available.
 */
export const resolveOrgUserDisplayName = (
  orgUser: KanbanOrganizationUserFragment
): string | null => {
  const attributes = attributesOf(orgUser);
  const fullName =
    `${attributeValue(attributes, "given_name")} ${attributeValue(
      attributes,
      "family_name"
    )}`.trim();
  return fullName || attributeValue(attributes, "email") || null;
};

/**
 * Build the `userId` → display-name map the trail resolves actors through.
 * @param orgUsers - Organization users from `ListKanbanOrganizationUsers`.
 * @returns The lookup map; ids with no derivable name are absent from it.
 */
export const buildActorNameMap = (
  orgUsers: readonly KanbanOrganizationUserFragment[]
): ReadonlyMap<string, string> =>
  new Map(
    orgUsers
      .map(orgUser => [orgUser.user?.id, resolveOrgUserDisplayName(orgUser)])
      .filter((pair): pair is [string, string] =>
        Boolean(pair[0] && pair[1])
      ) as readonly (readonly [string, string])[]
  );
